"use client";

import { useCallback, useEffect, useState } from "react";
import posthogSingleton from "posthog-js";
import { usePostHog } from "posthog-js/react";
import {
  readAdvertisingConsent,
  readAnalyticsConsent,
} from "@/components/analytics/browser-attribution";
import {
  subscribeToBrowserConsent,
  subscribeToBrowserConsentCategory,
} from "@/components/analytics/browser-consent-lifecycle";
import { withCurrentAttribution } from "@/components/marketing/attributed-link";
import { readBrowserDeviceType } from "@/lib/analytics/device-type";
import { POSTHOG_BROWSER_INITIALIZED_EVENT } from "@/lib/analytics/posthog-browser-lifecycle";
import { trackMicrosoftAdsEvent } from "@/lib/analytics/microsoft-uet";
import { AW_ID } from "@/lib/analytics/google-ids";
import {
  sendGoogleTagEventIfReady,
  subscribeToGoogleTagDestinationReady,
} from "@/lib/analytics/google-tag-readiness";

export interface AdsClickTrackingOptions {
  variant: string;
  ctaText: string;
  pageSlug?: string;
  ctaLocation?: string;
  eventName?: string;
  offer?: string;
  value?: number;
  currency?: string;
  funnelIntent?: string;
  plan?: string;
  emitSignupStart?: boolean;
}

interface AdsTrackingProps extends AdsClickTrackingOptions {
  ctaHref: string;
  children?: React.ReactNode;
  className?: string;
}

interface AdsTrackingLinkProps extends AdsClickTrackingOptions {
  ctaHref: string;
}

type PostHogLike =
  | {
      capture?: (
        event: string,
        properties?: Record<string, unknown>,
        options?: { send_instantly?: boolean; transport?: "sendBeacon" },
      ) => unknown;
    }
  | null
  | undefined;

const ATTRIBUTION_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const DEFAULT_CTA_CLASS =
  // tokens-allow-hex: green CTA — Tailwind v4 palette (green-600/700 OKLCH) diverges from these legacy sRGB hexes, so swapping to bg-done would be a visual change (out of scope)
  "inline-flex min-h-[48px] items-center justify-center rounded-lg bg-[#16a34a] px-8 py-4 text-lg font-bold text-white shadow-lg transition-all hover:bg-[#15803d] hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-done focus-visible:ring-offset-2";

function readCurrentSearchParams(): URLSearchParams | null {
  if (
    typeof window === "undefined" ||
    readAdvertisingConsent() !== "accepted"
  ) {
    return null;
  }
  return new URLSearchParams(window.location.search);
}

function buildTrackingPayload({
  variant,
  ctaText,
  pageSlug,
  ctaLocation,
  offer,
  value,
  currency,
  funnelIntent,
  plan,
}: AdsClickTrackingOptions): Record<string, string | number | boolean> {
  const params = readCurrentSearchParams();
  const payload: Record<string, string | number | boolean | undefined> = {
    variant,
    page_slug: pageSlug ?? variant,
    cta_location: ctaLocation,
    cta_label: ctaText,
    offer,
    value,
    currency,
    funnel_intent: funnelIntent,
    plan,
    ...(params
      ? {
          gclid_present: Boolean(params.get("gclid")),
          gbraid_present: Boolean(params.get("gbraid")),
          wbraid_present: Boolean(params.get("wbraid")),
          msclkid_present: Boolean(params.get("msclkid")),
        }
      : {}),
  };

  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const valueFromUrl = params?.get(key);
    if (!valueFromUrl) continue;
    payload[key] = valueFromUrl;
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as Record<string, string | number | boolean>;
}

export function AdsLandingViewTracker({
  variant,
  pageSlug,
  offer,
  funnelIntent,
  plan,
}: Pick<
  AdsClickTrackingOptions,
  "variant" | "pageSlug" | "offer" | "funnelIntent" | "plan"
>) {
  const posthog = usePostHog();

  useEffect(() => {
    const dedupeStem = `tsb_paid_landing_viewed:${pageSlug ?? variant}`;
    const gaKey = `${dedupeStem}:ga`;
    const posthogKey = `${dedupeStem}:posthog`;

    function buildLandingPayload() {
      return {
        ...buildTrackingPayload({
          variant,
          ctaText: "page_view",
          pageSlug,
          ctaLocation: "landing",
          offer,
          funnelIntent,
          plan,
        }),
        device_type: readBrowserDeviceType()?.toLowerCase() ?? "unknown",
      };
    }

    function captureLandingView() {
      const advertisingAccepted = readAdvertisingConsent() === "accepted";
      const analyticsAccepted = readAnalyticsConsent() === "accepted";
      if (!advertisingAccepted && !analyticsAccepted) return;

      if (advertisingAccepted && !sessionStorage.getItem(gaKey)) {
        if (
          sendGoogleTagEventIfReady("advertising", "paid_landing_viewed", {
            ...buildLandingPayload(),
            send_to: AW_ID,
          })
        ) {
          sessionStorage.setItem(gaKey, "1");
        }
      }

      if (
        analyticsAccepted &&
        !sessionStorage.getItem(posthogKey) &&
        posthogSingleton.__loaded &&
        posthog &&
        // An opted-out singleton drops the capture silently — consuming the
        // dedupe key then would permanently lose the landing view. The
        // provider's readiness re-emit is the retry moment.
        !posthogSingleton.has_opted_out_capturing?.()
      ) {
        posthog.capture("paid_landing_viewed", buildLandingPayload(), {
          send_instantly: true,
        });
        sessionStorage.setItem(posthogKey, "1");
      }
    }

    function onConsentChange() {
      // The provider's consent listener opts PostHog in on the same event. Run
      // after that synchronous listener so a fresh acceptance is not dropped.
      queueMicrotask(captureLandingView);
    }

    const unsubscribeConsent = subscribeToBrowserConsent(onConsentChange);
    const unsubscribeGoogleReady = subscribeToGoogleTagDestinationReady(
      "advertising",
      captureLandingView,
    );
    window.addEventListener(
      POSTHOG_BROWSER_INITIALIZED_EVENT,
      captureLandingView,
    );
    // Reconcile after listeners are attached. This closes the narrow race where
    // PostHog finishes between the effect's first readiness check and listener
    // registration, causing the one-shot browser-ready event to be missed.
    queueMicrotask(captureLandingView);
    return () => {
      unsubscribeConsent();
      unsubscribeGoogleReady();
      window.removeEventListener(
        POSTHOG_BROWSER_INITIALIZED_EVENT,
        captureLandingView,
      );
    };
  }, [funnelIntent, offer, pageSlug, plan, posthog, variant]);

  return null;
}

export function trackAdsCtaClick(
  options: AdsClickTrackingOptions,
  posthog?: PostHogLike,
): void {
  const advertisingAccepted = readAdvertisingConsent() === "accepted";
  const analyticsAccepted = readAnalyticsConsent() === "accepted";
  if (!advertisingAccepted && !analyticsAccepted) return;

  const payload = buildTrackingPayload(options);

  // Paid-network dispatch and paid URL identifiers belong to Advertising.
  if (advertisingAccepted) {
    if (options.emitSignupStart !== false) {
      sendGoogleTagEventIfReady("advertising", "sign_up_start", {
        value: 1,
        currency: "GBP",
        send_to: AW_ID,
      });
    }
    if (options.eventName) {
      sendGoogleTagEventIfReady("advertising", options.eventName, {
        ...payload,
        send_to: AW_ID,
      });
    }
  }
  if (advertisingAccepted && options.eventName) {
    trackMicrosoftAdsEvent(options.eventName, payload);
  }

  // Product-funnel analysis belongs to Analytics. When Advertising is off,
  // buildTrackingPayload deliberately excludes campaign and click identifiers.
  if (analyticsAccepted) {
    posthog?.capture?.("ads_cta_clicked", payload, {
      send_instantly: true,
      transport: "sendBeacon",
    });
  }
}

/**
 * Fires a single named GA4 event for a completed conversion (e.g. a lead form
 * POST that returned 2xx). Unlike trackAdsCtaClick this deliberately does NOT
 * fire `sign_up_start` or the `ads_cta_clicked` PostHog event — those carry
 * click semantics and would over-count if replayed at conversion time.
 */
export function trackAdsConversion(
  options: AdsClickTrackingOptions & { eventName: string },
  posthog?: PostHogLike,
): void {
  const advertisingAccepted = readAdvertisingConsent() === "accepted";
  const analyticsAccepted = readAnalyticsConsent() === "accepted";
  if (!advertisingAccepted && !analyticsAccepted) return;

  const payload = buildTrackingPayload(options);

  if (advertisingAccepted) {
    sendGoogleTagEventIfReady("advertising", options.eventName, {
      ...payload,
      send_to: AW_ID,
    });
  }
  if (advertisingAccepted) trackMicrosoftAdsEvent(options.eventName, payload);

  if (analyticsAccepted) {
    posthog?.capture?.(options.eventName, payload, {
      send_instantly: true,
      transport: "sendBeacon",
    });
  }
}

function useTrackedHref(ctaHref: string): string {
  const [href, setHref] = useState(ctaHref);

  useEffect(() => {
    const refreshHref = () => setHref(withCurrentAttribution(ctaHref));
    refreshHref();
    return subscribeToBrowserConsentCategory("advertising", refreshHref);
  }, [ctaHref]);

  return href;
}

export function AdsCtaButton({
  variant,
  ctaHref,
  ctaText,
  children,
  pageSlug,
  ctaLocation,
  eventName,
  offer,
  value,
  currency,
  funnelIntent,
  plan,
  emitSignupStart,
  className,
}: AdsTrackingProps) {
  const posthog = usePostHog();
  const href = useTrackedHref(ctaHref);

  const handleClick = useCallback(() => {
    trackAdsCtaClick(
      {
        variant,
        ctaText,
        pageSlug,
        ctaLocation,
        eventName,
        offer,
        value,
        currency,
        funnelIntent,
        plan,
        emitSignupStart,
      },
      posthog,
    );
  }, [
    posthog,
    variant,
    ctaText,
    pageSlug,
    ctaLocation,
    eventName,
    offer,
    value,
    currency,
    funnelIntent,
    plan,
    emitSignupStart,
  ]);

  return (
    <a
      href={href}
      onClick={handleClick}
      className={className ?? DEFAULT_CTA_CLASS}
    >
      {children || ctaText}
    </a>
  );
}

export function AdsMobileCtaBar({
  variant,
  ctaHref,
  ctaText,
  pageSlug,
  ctaLocation,
  eventName,
  offer,
  value,
  currency,
  funnelIntent,
  plan,
  emitSignupStart,
}: AdsTrackingLinkProps) {
  const posthog = usePostHog();
  const href = useTrackedHref(ctaHref);

  const handleClick = useCallback(() => {
    trackAdsCtaClick(
      {
        variant,
        ctaText,
        pageSlug,
        ctaLocation,
        eventName,
        offer,
        value,
        currency,
        funnelIntent,
        plan,
        emitSignupStart,
      },
      posthog,
    );
  }, [
    posthog,
    variant,
    ctaText,
    pageSlug,
    ctaLocation,
    eventName,
    offer,
    value,
    currency,
    funnelIntent,
    plan,
    emitSignupStart,
  ]);

  const ctaClassName =
    // tokens-allow-hex: green CTA — Tailwind v4 palette (green-600/700 OKLCH) diverges from these legacy sRGB hexes, so swapping to bg-done would be a visual change (out of scope)
    "flex min-h-[44px] w-full items-center justify-center rounded-lg bg-[#16a34a] px-4 py-3 text-base font-bold text-white transition-colors hover:bg-[#15803d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-done focus-visible:ring-offset-2";

  return (
    <a href={href} onClick={handleClick} className={ctaClassName}>
      {ctaText}
    </a>
  );
}
